/**
 * Siapa yang boleh menerima referensi gambar — dan keputusannya, terpisah dari layar.
 *
 * DIPINDAH KE SINI SUPAYA BISA DIUJI. Selama ia hidup sebagai satu baris di dalam
 * `PanelGambar`, satu-satunya cara memeriksanya adalah merender lalu MENGKLIK referensi
 * — dan `renderToString` tidak mengklik apa pun. Akibatnya baris itu tidak pernah
 * tersentuh pemeriksa mana pun, dan cacatnya bertahan sampai ada orang yang melihatnya
 * di layar sungguhan.
 *
 * TIDAK ADA DAFTAR MODEL DI SINI LAGI (ADR-073). Sampai 17 September berkas ini memuat
 * salinan `MODEL_TERIMA_REFERENSI` dan `MODEL_RASIO_PAKSA` dari `app/ai/image.py`, dijaga
 * sama oleh `periksa_referensi.mjs`. Kemampuan sekarang kolom `ai_models`, dan sampai ke
 * layar lewat `/dashboard/model` — `terima_referensi`, `image_aspect_ratios`, dan
 * seterusnya menempel di tiap baris `modelTenant`. Backend tetap yang menolak; yang di
 * sini cuma memperingatkan lebih awal, dari data yang SAMA.
 */

const cari = (modelTenant, model) => (modelTenant ?? []).find((m) => m?.model_id === model)

/** Nama model di daftar tenant yang menerima referensi — untuk kalimat peringatan. */
export function modelPenerimaReferensi(modelTenant) {
  return (modelTenant ?? []).filter((m) => m?.terima_referensi).map((m) => m.model_id)
}

/**
 * Apakah pilihan model sekarang bisa memakai referensi?
 *
 * CERMIN PERSIS `image.py::generate`, dan itu sebabnya ada dua cabang:
 *
 *   model dipilih   satu model, dan ia harus ada di daftar
 *   '' (Bawaan)     backend mengambil SELURUH rantai model tenant lalu MENYARINGNYA
 *                   (`if model else _get_image_models()`), jadi cukup ada satu yang bisa
 *
 * Versi pertama cuma memeriksa `daftar.includes(model)`. `model` berawal `''`, dan
 * string kosong tidak ada di daftar mana pun — jadi peringatan "model ini tidak menerima
 * gambar masukan" menyala untuk siapa pun yang memilih referensi tanpa pernah menyentuh
 * pemilih model, pada pilihan yang sebenarnya akan BERHASIL.
 *
 * Peringatan yang menyala saat tidak ada yang salah lebih mahal daripada tidak ada
 * peringatan: yang membacanya belajar mengabaikannya, termasuk saat ia benar.
 */
export function bisaPakaiReferensi(model, modelTenant) {
  if (model) return Boolean(cari(modelTenant, model)?.terima_referensi)
  return (modelTenant ?? []).some((m) => Boolean(m?.terima_referensi))
}

/**
 * Bentuk peringatan yang harus ditampilkan: `null`, `'model'`, atau `'bawaan'`.
 *
 * Dibedakan karena tindakannya berbeda. Model yang dipilih salah bisa diperbaiki sendiri
 * oleh yang memilihnya. Bawaan yang tidak punya satu pun model penerima referensi TIDAK
 * bisa — menyuruhnya "pilih gemini" percuma kalau gemini memang tidak ada di daftar model
 * tenant ini; yang bisa dilakukannya hanya meminta admin menambahkannya.
 */
export function peringatanReferensi(adaReferensi, model, modelTenant) {
  if (!adaReferensi) return null
  if (bisaPakaiReferensi(model, modelTenant)) return null
  return model ? 'model' : 'bawaan'
}

/**
 * Nomor urut sebuah referensi dalam pilihan — 1-based, atau 0 kalau tidak terpilih.
 *
 * URUTAN INI BERARTI. Referensi dikirim ke model sebagai larik berurutan, dan posisi di
 * dalamnya dibaca model sebagai arti — `ai/image.py` mencatat bahwa memindahkan teks saja
 * sudah mengubah tugasnya dari "buat gambar" jadi "analisis gambar".
 *
 * Sampai 15 September nomornya tidak pernah TAMPIL. Urutan kliknya terekam benar di
 * `refDipakai`, lalu dibuang backend oleh `WHERE id IN (...)` yang tidak menjaga urutan —
 * dan karena tidak ada nomor di layar, tidak ada yang bisa melihat bahwa urutannya salah.
 */
export function nomorRef(dipakai, id) {
  const i = (dipakai ?? []).indexOf(id)
  return i < 0 ? 0 : i + 1
}

/**
 * Geser satu referensi satu langkah, `arah` -1 (lebih awal) atau +1 (lebih akhir).
 *
 * Mengembalikan larik BARU; yang lama tidak disentuh. Bentuk yang menyunting di tempat
 * lalu memanggil `setState` dengan larik yang sama membuat React melihat rujukan yang
 * tidak berubah dan tidak merender ulang — layar diam padahal datanya sudah bergeser.
 *
 * Di luar batas: dikembalikan apa adanya, bukan dibungkus ke ujung yang lain. Menggeser
 * "lebih awal" pada yang sudah pertama lalu mendapatinya jadi terakhir adalah kejutan
 * yang tidak diminta siapa pun.
 */
export function geserRef(dipakai, id, arah) {
  const a = [...(dipakai ?? [])]
  const i = a.indexOf(id)
  const j = i + arah
  if (i < 0 || j < 0 || j >= a.length) return dipakai ?? []
  ;[a[i], a[j]] = [a[j], a[i]]
  return a
}

/**
 * Susun referensi untuk ditampilkan: bahan brand dulu, sesudahnya urutan dari server.
 *
 * CERMIN `daftar_referensi` di `app/web/v1/referensi.py`. Server sudah mengurutkannya
 * begitu; ini menjaga urutannya tetap benar kalau daftarnya disaring atau digabung di
 * layar — dan menjadikannya fungsi murni yang bisa diuji tanpa merender apa pun.
 */
export function susunReferensi(daftar) {
  const a = [...(daftar ?? [])]
  a.sort((x, y) => (y?.jenis === 'brand' ? 1 : 0) - (x?.jenis === 'brand' ? 1 : 0))
  return a
}

/**
 * Referensi yang harus TERPILIH sejak layar dibuka: bahan brand, paling banyak `maks`.
 *
 * KENAPA OTOMATIS. Gaya brand adalah hal yang diinginkan HAMPIR SELALU, dan yang
 * hampir selalu diinginkan tidak pantas jadi langkah manual yang harus diingat tiap
 * kali. Sebelum ini `refDipakai` mulai kosong, jadi tiap gambar yang dibuat tanpa
 * mengklik referensi lahir tanpa gaya brand sama sekali — dan tidak ada tanda apa pun
 * bahwa ada yang terlewat.
 *
 * HANYA yang ber-label `brand`. Bahan sekali pakai adalah bahan untuk SATU konten;
 * memilihnya otomatis untuk konten berikutnya akan mencampur foto produk lama ke
 * gambar yang tidak ada hubungannya.
 *
 * `maks` 1 secara bawaan (keputusan operator 16 September). Tiap referensi menambah
 * biaya panggilan, dan satu gambar brand sudah mengajarkan palet, gaya, dan komposisi
 * sekaligus.
 *
 * MURNI, dan itu penting: ia menentukan apa yang terkirim ke model pada tiap generasi,
 * jadi ia harus bisa diuji tanpa merender apa pun (XXIII-1).
 */
export function refBawaan(daftar, maks = 1) {
  return (daftar ?? [])
    .filter((r) => r?.jenis === 'brand' && r?.id)
    .slice(0, Math.max(0, maks))
    .map((r) => r.id)
}

/**
 * Susun daftar `bahan` untuk permintaan, dari dua sumber sekaligus.
 *
 * SATU DAFTAR BERURUTAN, karena urutan bahan dibaca model sebagai arti (lihat
 * `nomorRef`). "brand, foto saya, brand kedua" tidak bisa dinyatakan kalau kedua
 * sumber dikirim sebagai dua daftar terpisah.
 *
 * `dipakai` memuat id referensi tersimpan DAN kunci gambar temporary, bercampur dalam
 * urutan pilihan orang. Temporary dikenali dari awalan `temp:` — bukan dari mencocokkan
 * bentuk uuid, karena "bukan uuid" adalah definisi yang berubah artinya begitu bentuk
 * idnya berubah.
 *
 * MURNI: ia menentukan apa yang terkirim ke model pada tiap generasi, jadi ia harus bisa
 * diuji tanpa merender apa pun (XXIII-1).
 */
export const AWALAN_TEMP = 'temp:'

export function bentukBahan(dipakai, temp) {
  const peta = temp ?? {}
  return (dipakai ?? [])
    .map((k) => (String(k).startsWith(AWALAN_TEMP)
      ? (peta[k] ? { data: peta[k] } : null)
      : { id: k }))
    .filter(Boolean)
}

/**
 * Berapa slot yang masih tersisa. Empat total, dibagi kedua sumber.
 *
 * Dikatakan di layar, bukan dibiarkan jadi penolakan 422 sesudah orang memilih yang
 * kelima — batas yang baru terasa saat ditabrak adalah batas yang terasa seperti cacat.
 */
export const MAKS_BAHAN = 4

export function sisaSlot(dipakai) {
  return Math.max(0, MAKS_BAHAN - (dipakai ?? []).length)
}

/**
 * Rasio yang bisa dipilih untuk model yang sedang dipilih — atau `null` kalau tidak ada
 * pemilih yang jujur untuk ditampilkan.
 *
 * `null` UNTUK TIGA KEADAAN, dan ketiganya berarti pemilihnya disembunyikan:
 *   - "Bawaan" (`model` kosong): tiap model di rantai punya daftarnya sendiri, dan satu
 *     pilihan tidak mungkin sah untuk semuanya (gpt-5-image-mini tidak punya 4:5, gemini
 *     tidak punya auto). Backend juga menolak rasio tanpa model.
 *   - model tidak ada di daftar tenant
 *   - katalog belum tahu rasionya (`image_aspect_ratios` NULL)
 *
 * `abaikanDenganReferensi` TERUKUR, bukan diterbitkan: gpt-5-image-mini tetap 1024x1024
 * di jalur referensi berapa pun rasio yang diminta.
 */
export function pilihanRasio(modelTenant, model) {
  if (!model) return null
  const m = cari(modelTenant, model)
  if (!m?.image_aspect_ratios?.length) return null
  return {
    daftar: m.image_aspect_ratios,
    bawaan: m.default_image_aspect_ratio ?? null,
    abaikanDenganReferensi: m.has_reference_aspect_ratio === false,
  }
}

/**
 * Rasio yang BENAR-BENAR dikirim: pilihan orang kalau masih sah, selain itu `null`.
 *
 * Menjaga dua keadaan basi yang tidak terlihat di layar: rasio yang dipilih untuk model
 * sebelumnya lalu modelnya diganti (2:3 dari gpt dibawa ke gemini yang tidak punya 2:3),
 * dan rasio yang dipilih lalu referensi ditambahkan ke model yang mengabaikannya di
 * jalur itu. Keduanya akan ditolak 422 oleh backend — lebih baik tidak dikirim.
 */
export function rasioDikirim(pilihan, rasio, adaReferensi) {
  if (!pilihan || !rasio) return null
  if (!pilihan.daftar.includes(rasio)) return null
  if (adaReferensi && pilihan.abaikanDenganReferensi) return null
  return rasio
}

/**
 * Peringatan rasio, atau `null`: `'abaikan'` kalau referensi dipakai dengan model yang
 * TERUKUR mengabaikan rasio di jalur itu.
 *
 * DIKATAKAN SEBELUM MENEKAN BUAT. Keluhan yang melahirkannya: referensi 1080x1350 masuk,
 * 1024x1024 keluar, dan tidak ada apa pun di layar yang memberi tahu sebelumnya.
 *
 * Peringatan lama ("model ini selalu mengeluarkan 1024x768") DIBUANG, bukan dipindah.
 * Pengukuran 17 September menunjukkan flux dan seedream MENURUT `aspect_ratio` di jalur
 * tanpa referensi; "memaksa rasio" cuma terjadi saat rasio tidak dikirim sama sekali.
 */
export function peringatanRasio(modelTenant, model, adaReferensi) {
  if (!adaReferensi) return null
  return pilihanRasio(modelTenant, model)?.abaikanDenganReferensi ? 'abaikan' : null
}
